import "@fortawesome/fontawesome-free/css/all.min.css";

import * as Sentry from "@sentry/react";

import {
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";

import { Agentation } from "agentation";
import { ApolloWrapper } from "../app/javascript/components/util/apolloUtil";
import App from "../app/javascript/components/App";
import Auth0ProviderWithRedirectCallback from "./auth/Auth0ProviderWithRedirectCallback";
import ErrorPage from "../app/javascript/components/ErrorPage";
import { Provider } from "react-redux";
import React from "react";
import { createRoot } from "react-dom/client";
import { getConfig } from "@verse_core/shared_components/utils/envConfig";
import initTrackers from "../app/javascript/initTrackers";
import { pdfjs } from "react-pdf";
import { rebrandEnabled } from "../app/javascript/components/util/brandingFlag";
import { store } from "./redux/reduxStore";
import { takeGoLinkFragment } from "../app/javascript/components/login/goLinkFragment";

const appEnv = import.meta.env.VITE_APP_ENV as string | undefined;
const showAgentation =
  import.meta.env.DEV ||
  appEnv === "development" ||
  appEnv?.startsWith("staging");

// Agentation portals its toolbar to document.body and defaults to width:337px
// with content left-aligned. !tw-w-auto shrinks it so the trigger button hugs
// the right edge, matching the alignment of the Support and Dev Tool buttons.
function FloatingAgentation() {
  return (
    <Agentation className="!tw-bottom-[100px] !tw-right-[38px] !tw-flex !tw-w-auto !tw-justify-end" />
  );
}

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

// Lets CSS react to the rebrand flag; the palette keys off this in tailwind.preset.cjs.
if (rebrandEnabled()) {
  document.documentElement.setAttribute("data-rebrand", "true");
}

takeGoLinkFragment();
initTrackers();
(async () => {
  const config = await getConfig();
  createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <Sentry.ErrorBoundary
        fallback={<ErrorPage hideNav />}
        onError={(error) => {
          console.error(error);
          const versionSkewErrorMessage =
            "Failed to fetch dynamically imported module";
          if (
            error instanceof Error &&
            error.message.includes(versionSkewErrorMessage)
          ) {
            const params = new URLSearchParams(window.location.search);
            if (!params.has("error_retry")) {
              params.set("error_retry", "1");
              // Preserve hash if exists
              const hash = window.location.hash;
              setTimeout(() => {
                window.location.href = `${window.location.pathname}?${params.toString()}${hash}`;
              }, 100);
            } else {
              // If we've already retried, capture the error
              Sentry.captureException(error);
            }
          } else {
            // If it's not a version skew error, capture the error
            Sentry.captureException(error);
          }
        }}
      >
        <div className="tw-relative tw-block tw-min-h-[calc(87vh)]">
          <Provider store={store}>
            <RouterProvider
              router={createBrowserRouter(
                createRoutesFromElements(
                  <Route
                    path="/*"
                    errorElement={<ErrorPage hideNav />}
                    element={
                      <Auth0ProviderWithRedirectCallback
                        domain={config.AUTH_DOMAIN}
                        clientId={config.AUTH_VM_FE_CLIENT_ID}
                        audience={config.AUTH_AUDIENCE}
                      >
                        <ApolloWrapper>
                          <App />
                        </ApolloWrapper>
                      </Auth0ProviderWithRedirectCallback>
                    }
                  />,
                ),
              )}
            />
          </Provider>
          {showAgentation && (
            <>
              <div
                id="floating-stack"
                className="tw-pointer-events-none tw-fixed tw-bottom-[157px] tw-right-[38px] tw-z-[1000] tw-flex tw-flex-col tw-items-end"
              />
              <FloatingAgentation />
            </>
          )}
        </div>
      </Sentry.ErrorBoundary>
    </React.StrictMode>,
  );
})();
